import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import api from '../utils/api';
import { Compass, Target, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from '../utils/translations';

const AlignmentScore = () => {
  const { language } = useLanguage();
  const { t } = useTranslation(language);

  const [alignment, setAlignment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchAlignment = async (currentLanguage = language) => {
    setLoading(true);
    try {
      const response = await api.get(`/universe/alignment-score?language=${currentLanguage}`);
      setAlignment(response.data || null);
    } catch (error) {
      console.error('Failed to fetch alignment score', error);
      setAlignment(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async (currentLanguage = language) => {
    setHistoryLoading(true);
    try {
      const response = await api.get(`/universe/alignment-history?language=${currentLanguage}`);
      setHistory(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch alignment history', error);
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    fetchAlignment(language);

    if (showHistory) {
      fetchHistory(language);
    }
  }, [language, showHistory]);

  const toggleHistory = () => {
    setShowHistory((prev) => !prev);
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-emerald-400';
    if (score >= 60) return 'text-amber-400';
    if (score >= 40) return 'text-orange-400';
    return 'text-red-400';
  };

  const getScoreGradient = (score) => {
    if (score >= 80) return 'from-emerald-500 to-teal-500';
    if (score >= 60) return 'from-amber-500 to-yellow-500';
    if (score >= 40) return 'from-orange-500 to-amber-500';
    return 'from-red-500 to-orange-500';
  };

  const getScoreRingColor = (score) => {
    if (score >= 80) return '#10b981';
    if (score >= 60) return '#f59e0b';
    if (score >= 40) return '#f97316';
    return '#ef4444';
  };

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card p-6"
      >
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500" />
          <span className="ml-3 text-slate-400">{t('calculatingAlignment')}</span>
        </div>
      </motion.div>
    );
  }

  if (!alignment) return null;

  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (alignment.score / 100) * circumference;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-card p-6 border-violet-500/20"
      data-testid="alignment-score-card"
    >
      <div className="flex flex-col sm:flex-row items-center gap-6">
        <div className="relative flex-shrink-0">
          <svg className="w-28 h-28 transform -rotate-90">
            <circle
              cx="56"
              cy="56"
              r="45"
              stroke="currentColor"
              strokeWidth="8"
              fill="none"
              className="text-slate-700"
            />
            <circle
              cx="56"
              cy="56"
              r="45"
              stroke={getScoreRingColor(alignment.score)}
              strokeWidth="8"
              fill="none"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              className="transition-all duration-1000 ease-out"
            />
          </svg>

          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <span className={`text-3xl font-bold ${getScoreColor(alignment.score)}`}>
                {alignment.score}
              </span>
              <p className="text-xs text-slate-500">/100</p>
            </div>
          </div>
        </div>

        <div className="flex-1 text-center sm:text-left">
          <div className="flex items-center justify-center sm:justify-start gap-2 mb-2">
            <Compass className="w-5 h-5 text-violet-400" />
            <h3 className="font-heading text-lg font-bold text-white">{t('dailyAlignment')}</h3>
          </div>

          <div className={`inline-block px-3 py-1 rounded-full bg-gradient-to-r ${getScoreGradient(alignment.score)} mb-4`}>
            <span className="text-sm font-medium text-white">{alignment.energy_theme}</span>
          </div>

          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Target className="w-3.5 h-3.5 text-emerald-400" />
              </div>
              <div>
                <p className="text-xs text-emerald-400 font-medium mb-0.5">{t('focusOn')}</p>
                <p className="text-slate-300 text-sm">{alignment.focus_on}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
              </div>
              <div>
                <p className="text-xs text-amber-400 font-medium mb-0.5">{t('avoid')}</p>
                <p className="text-slate-300 text-sm">{alignment.avoid}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-white/5">
        <button
          onClick={toggleHistory}
          className="text-violet-400 hover:text-violet-300 transition-colors text-sm flex items-center gap-1 mx-auto"
          data-testid="alignment-history-toggle"
        >
          {showHistory ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          <span>{showHistory ? t('hideHistory') : t('viewHistory')} {t('alignmentHistory')}</span>
        </button>

        {showHistory && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="mt-4 space-y-2 max-h-48 overflow-y-auto"
          >
            {historyLoading && (
              <div className="text-center text-slate-400 py-3">
                {t('loading') || 'Loading...'}
              </div>
            )}

            {!historyLoading && history.length > 0 && history.slice(1).map((item, idx) => (
              <div
                key={item.id || idx}
                className="flex items-center justify-between py-2 px-3 bg-slate-800/30 rounded-lg"
                data-testid={`alignment-history-${idx}`}
              >
                <div className="flex items-center gap-3">
                  <span className={`text-lg font-bold ${getScoreColor(item.score)}`}>
                    {item.score}
                  </span>
                  <span className="text-slate-400 text-sm">{item.energy_theme}</span>
                </div>
                <span className="text-slate-600 text-xs">{item.date}</span>
              </div>
            ))}

            {!historyLoading && history.length === 0 && (
              <div className="text-center text-slate-500 py-3">
                {t('noHistoryYet') || 'No history yet'}
              </div>
            )}
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};

export default AlignmentScore;
